import { FocusEventHandler, useState } from 'react';
import MaskedInput from 'react-text-mask';

import { TextMaskProps } from './TextMaskProps.types';
import { useFixCaptureAttr } from './hooks/useFixCaptureAttr';

export const TimeMask = ({ inputRef, capture, is24HourFormat, ...restProps }: TextMaskProps) => {
  const fixedCapture = useFixCaptureAttr(capture);

  const [time, setTime] = useState(restProps.value?.toString());
  const [lastLength, setLastLength] = useState(0); // Track previous input length

  const timeMask = [/\d/, /\d/, ':', /[0-5]/, /\d/]; // Mask allowing single-digit hour

  const validateAndFormatTime = (input: string, autoFill = false) => {
    const cleanValue = input.replace(/\D/g, '').slice(0, 4); // Remove non-numeric chars, limit 4 digits

    if (cleanValue.length === 0) return ''; // Allow full delete

    let hh = cleanValue.slice(0, 2); // Allow 2-digit hours
    let mm = cleanValue.slice(2, 4);

    // Auto-fill minutes on blur
    if (autoFill && hh.length < 2) {
      hh = hh.padStart(2, '0');
    }

    if (autoFill && mm.length < 2) {
      mm = mm.padStart(2, '0');
    }

    // Validate hours
    const hour = parseInt(hh, 10);
    if (is24HourFormat) {
      if (hour > 23) hh = '23';
    } else {
      if (hour > 12) hh = '12';
    }

    // Validate minutes
    const minute = parseInt(mm, 10);
    if (minute > 59) mm = '59';

    return mm.length ? `${hh}:${mm}` : hh; // Keep clean format while typing
  };

  // Handle input change with real-time validation
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Allow deleting colon
    if (newValue.length < lastLength && lastLength === 5 && newValue.length === 2) {
      newValue = newValue[0]; // Delete the colon and last digit of hours
    }

    const formatted = validateAndFormatTime(newValue);

    e.target.value = formatted;
    setTime(formatted);
    setLastLength(newValue.length);

    if (restProps.onChange) {
      restProps.onChange(e);
    }
  };

  // Auto-fill missing digits on blur
  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    if (time) {
      const filled = validateAndFormatTime(time.toString(), true);
      e.target.value = filled;
      setTime(filled);
      if (restProps.onChange) {
        restProps.onChange(e);
      }
    }
    if (restProps.onBlur) {
      restProps.onBlur(e);
    }
  };

  return (
    <MaskedInput
      ref={ref => {
        inputRef(ref ? ref.inputElement : null);
      }}
      {...restProps}
      value={time}
      capture={fixedCapture}
      mask={timeMask}
      placeholder={is24HourFormat ? 'HH:mm' : 'hh:mm'}
      guide={false}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={restProps.disabled}
      style={{ width: 52 }}
    />
  );
};
